"use client";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { configApi } from "@/api/services";
import { beginConfigLoad, setApiKey, setMenuLink, setTenantId } from "@/api/http";
import { resolveSlug } from "@/lib/resolveSlug";
import { branchIdentifier } from "@/lib/fulfillment";
import { scopedStorageKey } from "@/lib/storageScope";
import { applySkin, clearSkin } from "@/lib/applySkin";
import { applyTenantPwa } from "@/lib/pwa";
import { readPreviewParams } from "@/lib/previewParams";
import { getSkin } from "@/lib/skins";
import { resolveAppMode, setAppMode, type AppMode } from "@/lib/app-mode";
import { useTable } from "@/store/table";
import type {
  Branch,
  OnlineOrderingConfig,
} from "@/api/online-ordering-service/model";
import {
  SELECTED_BRANCH_EVENT,
  readSelectedBranchId,
  readSelectedBranchSnapshot,
  persistSelectedBranchId,
} from "./branch-storage";

export type ActiveBranch = OnlineOrderingConfig["branches"][number] | Branch;

type BranchCtx = {
  /** Resolved branchId from config (preferred) — falls back to slug if unresolved. */
  branchId: string | null;
  /** Slug from URL or VITE_DEFAULT_BRANCH_SLUG (if any). */
  branchSlug: string | null;
  /** Full config payload from `configApi.get(urlPath)` (if loaded). */
  branchConfig: OnlineOrderingConfig | null;
  /**
   * What this storefront link is allowed to do, derived from the menu link
   * `type` plus table context. `"unresolved"` until config lands.
   */
  appMode: AppMode;
  /** Active branch config after map/nearest-branch selection fallback. */
  branch: ActiveBranch | null;
  selectedBranchId: string | null;
  selectBranch: (branchId: string | null, branch?: ActiveBranch | null) => void;
  requiresBranchSelection: boolean;
  loading: boolean;
  error: Error | null;
};

const BranchContext = createContext<BranchCtx | null>(null);



const DEFAULT_PRIMARY_VARS: Record<string, string> = {
  "--primary-50": "#e7f7ee",
  "--primary-100": "#c4ebd7",
  "--primary-200": "#96dab7",
  "--primary-300": "#65c995",
  "--primary-400": "#37b975",
  "--primary-500": "#0baa57",
  "--primary-600": "#09914a",
  "--primary-700": "#08793e",
  "--primary-800": "#066132",
};

type Rgb = { r: number; g: number; b: number };

function parseHexColor(raw: string | null | undefined): Rgb | null {
  if (!raw) return null;
  const s = raw.trim();
  const hex = s.startsWith("#") ? s.slice(1) : s;
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;
  const expanded =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => `${c}${c}`)
          .join("")
      : hex;
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function mix(a: Rgb, b: Rgb, amount: number): Rgb {
  const p = Math.max(0, Math.min(1, amount));
  return {
    r: Math.round(a.r + (b.r - a.r) * p),
    g: Math.round(a.g + (b.g - a.g) * p),
    b: Math.round(a.b + (b.b - a.b) * p),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function primaryScaleFrom(base: Rgb): Record<string, string> {
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  return {
    "--primary-50": toHex(mix(base, white, 0.9)),
    "--primary-100": toHex(mix(base, white, 0.76)),
    "--primary-200": toHex(mix(base, white, 0.58)),
    "--primary-300": toHex(mix(base, white, 0.38)),
    "--primary-400": toHex(mix(base, white, 0.18)),
    "--primary-500": toHex(base),
    "--primary-600": toHex(mix(base, black, 0.13)),
    "--primary-700": toHex(mix(base, black, 0.28)),
    "--primary-800": toHex(mix(base, black, 0.43)),
  };
}

function applyPrimaryScale(scale: Record<string, string>): void {
  const root = document.documentElement;
  Object.entries(scale).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const { tableInfo } = useTable();
  // Slug is resolved from hostname / pathname / env-var fallback. See
  // src/lib/resolveSlug.ts for the v1-equivalent rules.
  const slugResolution = useMemo(() => resolveSlug(), []);
  const branchSlug = slugResolution.slug;
  const routerBasename = slugResolution.basename;
  // Preview overrides from the URL — applied only when `mode=preview`,
  // never persisted. Captured once at mount because the iframe reloads
  // on URL change (so a remount picks up new params).
  const previewParams = useMemo(() => readPreviewParams(), []);

  const [branchConfig, setBranchConfig] = useState<OnlineOrderingConfig | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(!!branchSlug);
  const [error, setError] = useState<Error | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(() =>
    readSelectedBranchId(branchSlug),
  );
  const [selectedBranchSnapshot, setSelectedBranchSnapshot] =
    useState<ActiveBranch | null>(() => readSelectedBranchSnapshot(branchSlug));

  useEffect(() => {
    setSelectedBranchId(readSelectedBranchId(branchSlug));
    setSelectedBranchSnapshot(readSelectedBranchSnapshot(branchSlug));
  }, [branchSlug]);

  useEffect(() => {
    let cancelled = false;
    if (!branchSlug) {
      setBranchConfig(null);
      setLoading(false);
      setError(null);
      // Still resolve the apiKeyReady gate so the auth bootstrap doesn't
      // hang waiting for a config fetch that will never happen.
      setApiKey(null);
      setMenuLink(null);
      setTenantId(null);
      setAppMode("unresolved");
      return;
    }
    setLoading(true);
    setError(null);
    // Hold online-ordering reads until this slug's config publishes the key or
    // menu link; see beginConfigLoad.
    beginConfigLoad();
    (async () => {
      try {
        const cfg = await configApi.get(branchSlug);
        // In preview mode, overlay URL-supplied logoUrl/carouselImageUrls
        // on top of the saved config so the dashboard iframe reflects
        // unsaved changes. Skin is applied separately in the effect below.
        const merged: OnlineOrderingConfig = previewParams.isPreview
          ? ({
              ...(cfg as OnlineOrderingConfig),
              ...(previewParams.logoUrl
                ? { logoUrl: previewParams.logoUrl }
                : {}),
              ...(previewParams.carouselImageUrls
                ? { carouselImageUrls: previewParams.carouselImageUrls }
                : {}),
            } as OnlineOrderingConfig)
          : (cfg as OnlineOrderingConfig);
        if (!cancelled) {
          setBranchConfig(merged);
          // PWA: rewrite the document title, manifest <link>, icons,
          // theme-color meta, and apple-touch-icon to reflect this tenant.
          // The manifest link now points at the per-tenant backend endpoint
          // (so Chrome's WebAPK minting server can actually fetch it);
          // see src/lib/pwa.ts for the rationale.
          applyTenantPwa(merged, routerBasename, branchSlug);
          // Push the tenant API key + tenantId into the http module so
          // every subsequent /v1/online-ordering/* call gets X-API-KEY
          // injected automatically. Mirrors v1's apiKeyInterceptor.
          // Publish the app mode synchronously, in the same tick the config
          // lands and *before* the API key. `apiFetch`'s menu-only guard reads
          // this, and effects are too late: React runs child effects before
          // parent ones, so a screen firing a request on mount would slip past
          // a mode published from an effect below. `hasTable` is irrelevant to
          // the menu-only decision (configured type wins over `?tableId`), so
          // this is authoritative for the guard; the effect below only keeps
          // the dineIn/ordering distinction exact as table context changes.
          setAppMode(resolveAppMode(merged, !!tableInfo?.tableId));
          setMenuLink((cfg as { urlPath?: string | null }).urlPath ?? null);
          setApiKey((cfg as { apiKey?: string | null }).apiKey ?? null);
          const tid = (cfg as { tenantId?: string | null }).tenantId ?? null;
          setTenantId(tid);
          if (tid) {
            // Tag every subsequent PostHog event with the company group so
            // per-tenant metrics aggregate correctly in the unified project.
            void import("@/lib/analytics").then(({ setTenantGroup }) =>
              setTenantGroup(tid),
            );
          }
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          // TODO: surface a proper UI for unknown / inactive branch slugs.
          console.warn("[branch] configApi.get failed", err);
          setBranchConfig(null);
          setApiKey(null);
          setMenuLink(null);
          setTenantId(null);
          // Fail open: an unreachable config must not silently turn every
          // storefront into a read-only menu.
          setAppMode("unresolved");
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onSelectedBranch = () => {
      setSelectedBranchId(readSelectedBranchId(branchSlug));
      setSelectedBranchSnapshot(readSelectedBranchSnapshot(branchSlug));
    };
    window.addEventListener(SELECTED_BRANCH_EVENT, onSelectedBranch);
    window.addEventListener("storage", onSelectedBranch);
    return () => {
      window.removeEventListener(SELECTED_BRANCH_EVENT, onSelectedBranch);
      window.removeEventListener("storage", onSelectedBranch);
    };
  }, [branchSlug]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    // Resolution order:
    //   1. URL ?skin=<id> — dashboard/marketing live preview override.
    //   2. branchConfig.theme — saved skin id on the menu link.
    //   3. URL ?themeColor=<hex> — legacy preview override.
    //   4. branchConfig.colorScheme.primary / themeColor — saved color.
    // (1) and (2) apply a full token bundle; (3) and (4) fall back to
    // the legacy single-hex auto-scale so existing tenants keep their look.
    const preview = readPreviewParams();
    const urlSkin = getSkin(preview.skin);
    const configSkin = getSkin(
      (branchConfig as { theme?: string | null } | null)?.theme ?? null,
    );
    const skin = urlSkin ?? configSkin;
    if (skin) {
      applySkin(skin);
      return;
    }
    clearSkin();
    const primary =
      preview.themeColor ??
      branchConfig?.colorScheme?.primary ??
      (branchConfig as { themeColor?: string | null } | null)?.themeColor ??
      null;
    const parsed = parseHexColor(primary);
    applyPrimaryScale(parsed ? primaryScaleFrom(parsed) : DEFAULT_PRIMARY_VARS);
  }, [branchConfig]);

  const value = useMemo<BranchCtx>(() => {
    const configBranches = branchConfig?.branches ?? [];
    const findConfigBranch = (id: string | null | undefined) =>
      id
        ? configBranches.find((b) => {
            const fallbackId = (b as { id?: string | null }).id ?? null;
            return b.branchId === id || fallbackId === id;
          }) ?? null
        : null;

    const fallbackDineInBranch =
      tableInfo?.branchId && configBranches.length > 0
        ? ({
            ...configBranches[0],
            id: tableInfo.branchId,
            branchId: tableInfo.branchId,
            name: {
              en:
                tableInfo.tableName ||
                (configBranches[0] as any).branchName?.en ||
                (configBranches[0] as any).name?.en ||
                "Table",
            },
          } as ActiveBranch)
        : null;

    const tableBranch = findConfigBranch(tableInfo?.branchId) ?? fallbackDineInBranch;
    const selectedBranch = selectedBranchId
      ? findConfigBranch(selectedBranchId)
      : null;
    const snapshotBranch =
      selectedBranchId &&
      selectedBranchSnapshot &&
      branchIdentifier(selectedBranchSnapshot) === selectedBranchId
        ? selectedBranchSnapshot
        : null;
    const explicitConfigBranch = tableBranch ?? selectedBranch;
    const applicableSnapshot =
      snapshotBranch &&
      explicitConfigBranch &&
      branchIdentifier(snapshotBranch) === branchIdentifier(explicitConfigBranch)
        ? snapshotBranch
        : null;
    const explicitBranch = (explicitConfigBranch
      ? { ...explicitConfigBranch, ...(applicableSnapshot || {}) }
      : snapshotBranch) as ActiveBranch | null;
    const requiresBranchSelection =
      configBranches.length > 1 && !explicitBranch;
    const implicitSingleBranch =
      configBranches.length === 1 ? configBranches[0] : null;
    const previewBranch = requiresBranchSelection ? configBranches[0] ?? null : null;
    const activeBranch = explicitBranch ?? implicitSingleBranch ?? previewBranch;
    const resolvedBranchId = activeBranch
      ? branchIdentifier(activeBranch)
      : branchConfig
      ? null
      : branchSlug ?? null;
    return {
      branchId: resolvedBranchId,
      branchSlug,
      branchConfig,
      appMode: resolveAppMode(branchConfig, !!tableInfo?.tableId),
      branch: activeBranch,
      selectedBranchId,
      selectBranch: (nextBranchId, nextBranch) => {
        setSelectedBranchId(nextBranchId);
        setSelectedBranchSnapshot(nextBranch ?? null);
        persistSelectedBranchId(nextBranchId, nextBranch, branchSlug);
      },
      requiresBranchSelection,
      loading,
      error,
    };
  }, [
    branchConfig,
    branchSlug,
    loading,
    error,
    selectedBranchId,
    selectedBranchSnapshot,
    tableInfo?.branchId,
    tableInfo?.tableId,
  ]);

  // Keep the module-level cell in step with table context. The config-fetch
  // path above already publishes the menu-only decision; this only refines
  // dineIn vs ordering, which the network guard does not act on.
  useEffect(() => {
    setAppMode(value.appMode);
  }, [value.appMode]);

  return (
    <BranchContext.Provider value={value}>{children}</BranchContext.Provider>
  );
}

export function useBranch(): BranchCtx {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used within a BranchProvider");
  return ctx;
}
